import { Alert, FaroCheckbox, FaroDialog, TextField } from "@faro-lotv/flat-ui";
import { Stack } from "@mui/system";
import { useState, type JSX } from "react";
import { useUploadElement } from "./use-upload-element";
import { UploadElementType } from "./use-upload-element-types";

type CadUploadDialogProps = {
  /** The CAD file to upload */
  file: File;
  /** Method to set the CAD modelfile */
  setCadFile(file: File | undefined): void;
  /** Callback to call when the file is uploaded */
  onFileUploaded?(): void;
};

/**
 * @returns a dialog for uploading CAD files.
 * Allows user to set CAD spatial grid generation options.
 */
export function CadUploadDialog({
  file,
  setCadFile,
  onFileUploaded,
}: CadUploadDialogProps): JSX.Element {
  const defaultStaticChunks = 3;
  const minStaticChunks = 1;
  const maxStaticChunks = 10;
  const [staticChunks, setStaticChunks] = useState<number | undefined>(
    defaultStaticChunks,
  );

  const defaultChunkMB = 5;
  const minChunkMB = 0.1;
  const maxChunkMB = 50;
  const [staticChunkMB, setStaticChunkMB] = useState<number | undefined>(
    defaultChunkMB,
  );
  const [dynamicChunkFileMB, setDynamicChunkFileMB] = useState<
    number | undefined
  >(defaultChunkMB);
  const [dynamicChunkMemoryMB, setDynamicChunkMemoryMB] = useState<
    number | undefined
  >(defaultChunkMB);
  const [doNotCollateSmallChunks, setDoNotCollateSmallChunks] = useState(false);

  const defaultCellWidth = 10;
  const minCellWidth = 0.1;
  const maxCellWidth = 100;
  const [cellWidth, setCellWidth] = useState<number | undefined>(
    defaultCellWidth,
  );
  const uploadElement = useUploadElement();
  return (
    <FaroDialog
      title="Import CAD Model"
      open
      isConfirmDisabled={
        staticChunks === undefined ||
        staticChunkMB === undefined ||
        dynamicChunkFileMB === undefined ||
        cellWidth === undefined
      }
      onConfirm={() => {
        uploadElement({
          type: UploadElementType.cad,
          file,
          name: file.name || "",
          createdAt: new Date(file.lastModified),
          onFileUploaded,
          cadSpatialGridOption: {
            numberOfStaticChunks: staticChunks ?? defaultStaticChunks,
            staticChunkByteSize:
              (staticChunkMB ?? defaultChunkMB) * 1024 * 1024,
            dynamicChunkByteFileSize:
              (dynamicChunkFileMB ?? defaultChunkMB) * 1024 * 1024,
            dynamicChunkByteMemorySize: dynamicChunkMemoryMB
              ? dynamicChunkMemoryMB * 1024 * 1024
              : undefined,
            gridCellWidth: cellWidth ?? defaultCellWidth,
            doNotCollateSmallChunks,
          },
        });
        setCadFile(undefined);
      }}
      onCancel={() => {
        setCadFile(undefined);
      }}
    >
      <Stack spacing={1}>
        <Alert
          variant="info"
          title={
            <>
              This is for internal testing purpose! <br />
              Default parameters should work for most CAD/BIM models. Adjusting
              these values to change the spatial grid submeshes generated by
              backend.
            </>
          }
        />
        <NumberInputField
          label="Maximum number of static chunks"
          value={defaultStaticChunks}
          minValue={minStaticChunks}
          maxValue={maxStaticChunks}
          setValue={setStaticChunks}
        />
        <NumberInputField
          label="Static chunk size (MB)"
          value={defaultChunkMB}
          minValue={minChunkMB}
          maxValue={maxChunkMB}
          setValue={setStaticChunkMB}
        />
        <NumberInputField
          label="Dynamic chunk size on file (MB)"
          value={defaultChunkMB}
          minValue={minChunkMB}
          maxValue={maxChunkMB}
          setValue={setDynamicChunkFileMB}
        />
        <NumberInputField
          label="Minimum width of grid cells (Meter)"
          value={defaultCellWidth}
          minValue={minCellWidth}
          maxValue={maxCellWidth}
          setValue={setCellWidth}
        />
        <NumberInputField
          label="Optional dynamic chunk size in memory (MB)"
          value={undefined}
          minValue={minChunkMB}
          maxValue={maxChunkMB}
          setValue={setDynamicChunkMemoryMB}
          optional
        />
        <FaroCheckbox
          checked={doNotCollateSmallChunks}
          onChange={(event) => setDoNotCollateSmallChunks(event.target.checked)}
          label="Do not collate small chunks"
        />
      </Stack>
    </FaroDialog>
  );
}

type NumberInputFieldProps = {
  setValue(value: number | undefined): void;
  value: number | undefined;
  minValue: number;
  maxValue: number;
  label: string;
  optional?: boolean;
};
/**
 * @returns A number input field for CAD upload dialog
 */
function NumberInputField({
  setValue,
  value,
  minValue,
  maxValue,
  label,
  optional,
}: NumberInputFieldProps): JSX.Element {
  const [inputText, setInputText] = useState<string>(`${value}`);
  const [errorText, setErrorText] = useState<string>("");
  return (
    <TextField
      label={label}
      text={inputText}
      onTextChanged={(newText: string) => {
        setInputText(newText);
        setErrorText("");
        if (optional && !newText) {
          setValue(undefined);
        }
        const newValue = Number(newText);
        if (isNaN(newValue) || newValue < minValue || newValue > maxValue) {
          setErrorText(
            `Value must be number between ${minValue} and ${maxValue}`,
          );
          setValue(undefined);
        } else {
          setValue(newValue);
        }
      }}
      error={errorText}
      onFocus={(ev) => {
        ev.target.select();
      }}
    />
  );
}
